import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import Alert from '../components/common/Alert';
import api from '../services/api';
import KeyboardDismissView from '../components/common/KeyboardDismissView';
import { useFormNavigation } from '../hooks/useFormNavigation';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        await api.get(`/auth/reset-password/${token}`);
        setTokenValid(true);
      } catch {
        setTokenValid(false);
        setError('This password reset link is invalid or has expired.');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setError('Invalid reset link.');
    }
  }, [token]);

  const { registerField, focusNext } = useFormNavigation({
    onSubmit: () => document.getElementById('reset-submit')?.click()
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationError('');
  };

  const validate = () => {
    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setValidationError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setValidationError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setValidationError('Password must contain at least one number');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setValidationError('Password must contain at least one special character');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError('');

    try {
      await api.post(`/auth/reset-password/${token}`, {
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-[100dvh] bg-surface-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-surface-900 mx-auto mb-4"></div>
          <p className="text-surface-500">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-[100dvh] bg-surface-50 flex">
        {/* Left Panel */}
        <div
          className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
          style={{ backgroundColor: 'var(--sidebar-bg)' }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
            <div
              className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            />
          </div>

          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
            <div className="mb-8">
              <img
                src="/logo.png"
                alt="Hisab Journey"
                className="w-14 h-14 mb-6 rounded-full"
              />
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                Password<br />reset complete
              </h1>
              <p className="text-surface-400 text-lg mt-4 max-w-md">
                Your password has been successfully changed.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Success */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900 mb-4">
              Password changed!
            </h2>
            <p className="text-surface-500 mb-8">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-3.5 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group"
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)'
              }}
            >
              Continue to login
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-[100dvh] bg-surface-50 flex">
        {/* Left Panel */}
        <div
          className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
          style={{ backgroundColor: 'var(--sidebar-bg)' }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-expense-500 rounded-full blur-3xl" />
            <div
              className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            />
          </div>

          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
            <div className="mb-8">
              <img
                src="/logo.png"
                alt="Hisab Journey"
                className="w-14 h-14 mb-6 rounded-full"
              />
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                Link expired<br />or invalid
              </h1>
              <p className="text-surface-400 text-lg mt-4 max-w-md">
                Password reset links expire after 1 hour for security reasons.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Invalid Token */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900 mb-4">
              Invalid or expired link
            </h2>
            <p className="text-surface-500 mb-8">
              This password reset link is no longer valid. Please request a new one.
            </p>

            <div className="space-y-4">
              <Link
                to="/forgot-password"
                className="w-full py-3.5 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group"
                style={{
                  backgroundColor: 'var(--accent-bg)',
                  color: 'var(--accent-text)'
                }}
              >
                Request new reset link
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to="/login"
                className="w-full py-3.5 rounded-xl font-medium border border-surface-200 text-surface-700 hover:bg-surface-100 transition-all flex items-center justify-center"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-[100dvh] bg-surface-50 flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden xl:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: 'var(--sidebar-bg)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-income-500 rounded-full blur-3xl" />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Hisab Journey"
              className="w-14 h-14 mb-6 rounded-full"
            />
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Create new<br />password
            </h1>
            <p className="text-surface-400 text-lg mt-4 max-w-md">
              Choose a strong password to keep your account secure.
            </p>
          </div>

          <div className="space-y-4 mt-8">
            {[
              'At least 8 characters',
              'Include uppercase & lowercase',
              'Add numbers & special characters'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-surface-300">
                <div className="w-5 h-5 rounded-full bg-income-500/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-income-500" />
                </div>
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Reset Form */}
      <KeyboardDismissView className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-y-auto safe-area-top">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="xl:hidden text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
              style={{ backgroundColor: 'var(--sidebar-bg)' }}
            >
              <span className="text-xl font-bold" style={{ color: 'var(--accent-text)' }}>HJ</span>
            </div>
            <h1 className="text-xl font-bold text-surface-900">Hisab Journey</h1>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-900">
              Set new password
            </h2>
            <p className="text-surface-500 mt-2">
              Your new password must be different from previously used passwords.
            </p>
          </div>

          {(error || validationError) && (
            <Alert
              variant="error"
              message={validationError || error}
              onClose={() => {
                setError('');
                setValidationError('');
              }}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-5 mt-6">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('password', el)}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min. 8 characters"
                  enterKeyHint="next"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), focusNext('password'))}
                  className="w-full pl-12 pr-12 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Confirm new password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  ref={(el) => registerField('confirmPassword', el)}
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  enterKeyHint="done"
                  className="w-full pl-12 pr-12 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-surface-900 focus:border-transparent transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-surface-50 rounded-xl p-4">
              <p className="text-sm font-medium text-surface-700 mb-2">Password requirements:</p>
              <ul className="text-sm text-surface-500 space-y-1">
                <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                  • At least 8 characters
                </li>
                <li className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                  • One uppercase letter
                </li>
                <li className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                  • One lowercase letter
                </li>
                <li className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                  • One number
                </li>
                <li className={/[^A-Za-z0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                  • One special character
                </li>
              </ul>
            </div>

            {/* Submit */}
            <button
              id="reset-submit"
              type="submit"
              className="w-full py-3.5 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)'
              }}
              disabled={loading}
            >
              {loading ? (
                'Resetting...'
              ) : (
                <>
                  Reset password
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>
      </KeyboardDismissView>
    </div>
  );
}
